import React, {useEffect, useState, useRef, useCallback} from 'react';
import {Routes, Route, useNavigate} from 'react-router-dom';
import ModelList from "./ModelList";
import './ViewerApp.css';
import Login from "./Login";
import Register from "./Register";
import GetAccessToken from './Tokens'
import ProgressBar from "./ProgressBar";
import PartList from "./PartList";
import RotationInput from "./RotationInput";
import GridSetting from "./GridSetting.js";
import gridIcon from "./icons/grid.png";
import lockIcon from "./icons/rotation-lock.png";
import homeIcon from "./icons/home.png";
import BackgroundSetting from "./BackgroundSetting";
import { useConfig } from './ConfigContext';

const WebGLComponent = ({setParts, model, setModel, rotationEnabled, setRotationEnabled, getRotationAngle, webglUrl, setWebglUrl}) => {
    const [progress, setProgress] = useState(null);
    const [webGLReady, setWebGLReady] = useState(false);
    const iframeRef = useRef(null);
    const [progression, setProgression] = useState(false);
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);
    const config = useConfig();

    useEffect(() => {
        const messageHandler = (event) => {
            // checking event.origin === '' should be added in future
            if (event.data.type === "webgl_ready") {
                setWebGLReady(true);
            }
            else if (event.data.type === "download_progress") {
                setProgress(event.data.value * 100);
                if (event.data.value === 1) {
                    const timer = setTimeout(() => {
                        setProgression(false);
                    }, 1000);
                    return () => clearTimeout(timer);
                }
            }
            else if (event.data.type === "model_parts") {
                setParts(JSON.parse(event.data.text));
            }
            else if (event.data.type === "toggle_rotation_input") {
                setRotationEnabled(!rotationEnabled);
            }
            else if (event.data.type === "get_rotation_angles") {
                let angles = JSON.parse(event.data.text);
                getRotationAngle(angles);
            }
            else if (event.data.type === "store_data") {
                localStorage.setItem(event.data.key, event.data.value);
            }
            else if (event.data.type === "get_data") {
                const value = localStorage.getItem(event.data.key);
                window.webglIframe.contentWindow.postMessage({type: 'webgl_return_data', value: value}, webglUrl);
            }
            else if (event.data.type === "store_file_data") {
                const key = event.data.key;
                const fileData = event.data.file;
                let dbRequest = indexedDB.open('UnityFileDB', 1);
                dbRequest.onupgradeneeded = function(dBReqeustEvent) {
                    let db = dBReqeustEvent.target.result;
                    db.createObjectStore('files');
                };
                dbRequest.onsuccess = function(dBReqeustEvent) {
                    let db = dBReqeustEvent.target.result;
                    let transaction = db.transaction(['files'], 'readwrite');
                    let store = transaction.objectStore('files');
                    store.put(fileData, key);
                };
                dbRequest.onerror = function(dBReqeustEvent) {
                    console.error("IndexedDB error:", dBReqeustEvent.target.errorCode);
                };

            }
            else if (event.data.type === "get_file_data") {
                const key = event.data.key;
                let dbRequest = indexedDB.open('UnityFileDB', 1);
                dbRequest.onsuccess = function(dBReqeustEvent) {
                    let db = dBReqeustEvent.target.result;
                    let transaction = db.transaction(['files'], 'readonly');
                    let store = transaction.objectStore('files');
                    let getRequest = store.get(key);
                    getRequest.onsuccess = function(dBGetEvent) {
                        let data = dBGetEvent.target.result;
                        window.webglIframe.contentWindow.postMessage({type: 'webgl_return_file_data', value: data}, webglUrl);
                    };
                };
            }

        };

        window.addEventListener('message', messageHandler);

        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, [rotationEnabled, webglUrl]);


    useEffect( () => {
        if (model != null){
            setProgression(true);
        }
    }, [model]);

    useEffect(() => {
        if(webglUrl !== "" && model && webGLReady) {
            window.webglIframe.contentWindow.postMessage({type: 'webgl_get_model', name: model, token: ""}, webglUrl);
        }
    }, [webglUrl, model, webGLReady]);


    useEffect(() => {
        if (config) {
            setWebglUrl(config["webgl_url"]);
            if (params)
                setModel(params.get('name'));
        }

    }, [params, config]);


    if (iframeRef.current) {
        window.webglIframe = iframeRef.current;
    }


    if (webglUrl !== "") {
        return (
            <div className="iframe-container">
                <iframe
                    name={Date.now().toString()}
                    ref={iframeRef}
                    src={webglUrl}
                    title="Unity WebGL"
                />
                {progression && progress >= 0 && <div style={{width: '60dvw'}}>
                    <ProgressBar progress={progress}/>
                </div>}
            </div>
        )
    }

    return null
};

function WebGLAppContainer() {
    const [parts, setParts] = useState(null);
    const [model, setModel] = useState(null);
    const [rotationEnabled, setRotationEnabled] = useState(false);
    const [inputFieldRotationAngle, setInputFieldRotationAngle] = useState(null);
    const [showGismoSetting, setShowGismoSetting] = useState(false);
    const [showStructureList, setShowStructureList] = useState(false);
    const [showBackgroundSetting, setBackgroundColor] = useState(false);
    const [showGridIcons, setShowGridIcons] = useState(false);
    const [webglUrl, setWebglUrl] = useState("");
    const refRotationLockX = useRef(null);
    const refRotationLockY = useRef(null);
    const refGismoXY = useRef(null);
    const refGismoXZ = useRef(null);
    const refGismoYZ = useRef(null);
    const [selectedXY, setSelectedXY] = useState(false);
    const [selectedYZ, setSelectedYZ] = useState(false);
    const [selectedXZ, setSelectedXZ] = useState(false);
    const navigate = useNavigate();
    const [isRelatedListOpen, setRelatedListOpen] = useState(false)
    const [isGroupListOpen, setGroupListOpen] = useState(false)
    const [specimenServiceUrl, setSpecimenServiceUrl] = useState(null);
    const [specimenName, setSpecimenName] = useState(null);
    const [relatedSpecimens, setRelatedSpecimens] = useState(null);
    const [groupSpecimens, setGroupSpecimens] = useState(null);
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);
    const config = useConfig();

    useEffect(() => {
        if (config) {
            setSpecimenServiceUrl(config["model_service_url"]);
        }
    }, [config]);

    useEffect(() => {
        const handleContextMenu = (event) => {
            event.preventDefault();
        };

        document.addEventListener('contextmenu', handleContextMenu);
    }, []);

    useEffect(() => {
        if(showGridIcons === false)
            return;
        if (selectedXY)
            refGismoXY.current.classList.add('grid-button-selected');
        else if(selectedXZ)
            refGismoXZ.current.classList.add('grid-button-selected');
        else if(selectedYZ)
            refGismoYZ.current.classList.add('grid-button-selected');
    }, [showGridIcons]);

    const handleSpecimenSelect = (specimen_name) => {
        window.location.href = "/viewer?name=" + specimen_name;
    }

    const handleRotationLock = (axis, ref) => {
        if (ref.current.classList.contains("bottom-toolbox-button-selected")) {
            ref.current.classList.remove('bottom-toolbox-button-selected');
        }
        else {
            ref.current.classList.add('bottom-toolbox-button-selected');
        }
        window.webglIframe.contentWindow.postMessage({type: 'webgl_toggle_lock_rotation', axis: axis}, webglUrl);
    };

    const handleGismoIcon = (show) => {
        setShowGridIcons(show);
    };

    const handleHomeIcon = () => {
        window.location.href = "/";
    };

    const handleGismoToggle = (axis, ref, selectedButtonFlag, setSelectedButtonFlag) => {
        if (selectedButtonFlag) {
            ref.current.classList.remove('grid-button-selected');
            setSelectedButtonFlag(false);
        }
        else {
            refGismoXY.current.classList.remove('grid-button-selected');
            refGismoYZ.current.classList.remove('grid-button-selected');
            refGismoXZ.current.classList.remove('grid-button-selected');
            setSelectedXY(false);
            setSelectedYZ(false);
            setSelectedXZ(false);
            setSelectedButtonFlag(true);
            ref.current.classList.add('grid-button-selected');
        }
        window.webglIframe.contentWindow.postMessage({type: 'webgl_toggle_grid', axis: axis}, webglUrl);
    };

    const getSpecimenList = useCallback((serviceUrl, setSpecimenList) => {
        if (config) {
            fetch(serviceUrl, {
            })
                .then(response => {
                    return response.json();
                })
                .then(data => setSpecimenList(data.specimens.filter(model => model)))
                .catch(error => {
                    if (error.message !== 'Unauthorized') {
                        console.error('Error fetching the models:', error);
                    }
                });
        }
    }, [config]);

    useEffect(() => {
        if (specimenServiceUrl != null) {
            getSpecimenList(specimenServiceUrl + "/public/related?specimen_name=" + specimenName, setRelatedSpecimens);
            getSpecimenList(specimenServiceUrl + "/public/sameGroup?specimen_name=" + specimenName, setGroupSpecimens);
        }
    }, [specimenServiceUrl, specimenName]);

    const handleSpecimenRelatedList = () => {
        if(isGroupListOpen)
            setGroupListOpen(false);
        setRelatedListOpen(!isRelatedListOpen);
    }

    const handleSpecimenGroupList = () => {
        if(isRelatedListOpen)
            setRelatedListOpen(false);
        setGroupListOpen(!isGroupListOpen);
    }

    const handleSetBackgroundColor = () => {

    }

    useEffect(() => {
        if (config) {
            setWebglUrl(config["webgl_url"]);
            if (params)
                setSpecimenName(params.get('name'));
        }

    }, [params, config]);

    return (
        <div style={{
            display: "flex", flexDirection: "row", flexWrap: "nowrap"
        }
        }>
            <div className="app-header">
                <button ref={refRotationLockX} onClick={handleHomeIcon} className="home-button"><img src={homeIcon} alt="lock-x-icon"></img></button>
                <a className="app-header-title">Maastricht University Specimen Viewer</a>
            </div>
            <div className="app-container">
                <div className="toolbar-container">
                    <button className="toolbar-left-button" onClick={() => setShowStructureList(!showStructureList)}>Structure List</button>
                    <button className="toolbar-left-button" onClick={() => setRotationEnabled(!rotationEnabled)}>Rotation Input</button>
                    <button className="toolbar-left-button" onClick={() => setShowGismoSetting(!showGismoSetting)}>Grid Setting</button>
                    <button className="toolbar-left-button" onClick={() => setBackgroundColor(!showBackgroundSetting)}>Background</button>
                    <button className="toolbar-left-button"> Help</button>
                    <div style={{position: "absolute", right : "35px", display: "flex"}}>
                        <button className="toolbox-right-button" onClick={() => handleSpecimenRelatedList()}>Related Specimens</button>
                        <button className="toolbox-right-button" onClick={() => handleSpecimenGroupList()}>Other Specimens in Group</button>
                    </div>
                </div>
                <div className="main-container">
                    {
                        (showStructureList && parts && <div className="structure-list-container">
                                <PartList parts={parts}/></div>
                        )
                    }
                    <div className="setting-container">
                        {
                            (rotationEnabled && <RotationInput angles={inputFieldRotationAngle} setRotationEnabled={setRotationEnabled}/>)
                        }
                        {
                            (showGismoSetting && <GridSetting/>)
                        }
                        {
                            (showBackgroundSetting && <BackgroundSetting/>)
                        }
                    </div>
                    <div className="webgl-container">
                        <WebGLComponent setParts={setParts} model={model} setModel={setModel} rotationEnabled={rotationEnabled}
                                        setRotationEnabled={setRotationEnabled} getRotationAngle={setInputFieldRotationAngle} webglUrl={webglUrl} setWebglUrl={setWebglUrl}/>
                    </div>
                    {
                        (isRelatedListOpen && <div className="list-container">
                                <ModelList setModel={handleSpecimenSelect} listTitle={"Related Specimens"} specimenList={relatedSpecimens}/></div>
                        )
                    }
                    {
                        (isGroupListOpen && <div className="list-container">
                                <ModelList setModel={handleSpecimenSelect} listTitle={"Other Specimens in Group"} specimenList={groupSpecimens}/></div>
                        )
                    }
                </div>
                <div className="bottom-toolbox-container">
                    <image onMouseEnter={() => handleGismoIcon(true)} onMouseLeave={() => handleGismoIcon(false)} className="bottom-toolbox-button">
                        {(showGridIcons &&
                        <div>
                            <button ref={refGismoXY} className="grid-button" onClick={() => handleGismoToggle('xy', refGismoXY, selectedXY, setSelectedXY)}>XY</button>
                            <button ref={refGismoYZ} className="grid-button" onClick={() => handleGismoToggle('yz', refGismoYZ, selectedYZ, setSelectedYZ)}>YZ</button>
                            <button ref={refGismoXZ} className="grid-button" onClick={() => handleGismoToggle('xz', refGismoXZ, selectedXZ, setSelectedXZ)}>XZ</button>
                        </div>
                        )}
                        <img src={gridIcon} alt="grid-icon"></img>
                        <a> Grid </a>
                    </image>
                    <button ref={refRotationLockX} onClick={() => handleRotationLock('x', refRotationLockX)} className="bottom-toolbox-button"><img src={lockIcon} alt="lock-x-icon"></img>Lock X Axis </button>
                    <button ref={refRotationLockY} onClick={() => handleRotationLock('y', refRotationLockY)} className="bottom-toolbox-button"><img src={lockIcon} alt="lock-y-icon"></img>Lock Y Axis </button>
                </div>
                <div id="unity-footer">
                    <div id="unity-webgl-logo"></div>
                </div>
            </div>
        </div>
    )
}

export default WebGLAppContainer;
